/**
 * Region filter shared by the geography page (which builds the links)
 * and the profile journal (which honors them) — V1's zoom-to-region
 * (user-panel-geography.js filterYoinksByRegion/getRegionFilterLabel),
 * un-deferred 2026-07-16 on user request. The filter rides the URL as
 * one shareable param: ?region=Country~State~County (the 2026-07-13
 * "shared URLs carry the full view state" rule), '~' because it's
 * never percent-encoded and never appears in place names.
 */

import { foldRegionName } from '../../../assets/js/utils.js';

const SEP = '~';

// Accent/case-blind name compare: the stats page merges "Bolívar" and
// "Bolivar" into one region, so the filter must match every stored variant.
const sameRegion = (a, b) => foldRegionName(a) === foldRegionName(b);

/** {country, state?, county?} → the ?region= param value. */
export function regionParam(region) {
  if (!region?.country) return '';
  return [region.country, region.state, region.county]
    .filter(Boolean)
    .join(SEP);
}

/** The ?region= param value back to {country, state, county} (or null). */
export function parseRegionParam(value) {
  if (!value) return null;
  const [country, state, county] = value.split(SEP).map((s) => s.trim());
  if (!country) return null;
  return { country, state: state || null, county: county || null };
}

/**
 * V1's exact narrowing (user-panel-geography.js:50-68): a county filter
 * matches all three fields, a state filter two, a country filter one.
 */
export function filterYoinksByRegion(yoinks, region) {
  if (!region) return yoinks;
  return yoinks.filter((yoink) => {
    const loc = yoink.locations;
    if (!loc) return false;
    if (region.county) {
      return sameRegion(loc.country, region.country) &&
             sameRegion(loc.state_province, region.state) &&
             sameRegion(loc.county, region.county);
    }
    if (region.state) {
      return sameRegion(loc.country, region.country) &&
             sameRegion(loc.state_province, region.state);
    }
    return sameRegion(loc.country, region.country);
  });
}

/** Human label, most specific first — V1's getRegionFilterLabel. */
export function regionLabel(region) {
  if (!region) return '';
  if (region.county) return `${region.county}, ${region.state}`;
  if (region.state) return `${region.state}, ${region.country}`;
  return region.country;
}
